import { memo, useCallback } from 'react';
import { Box, IconButton, TableBody, TableRow } from '@mui/material';
import CustomTableCell from 'components/cmn/CustomTableCell';
import { useProject } from 'hooks/useProject';
import { ICmnTableBodyProps, Order } from 'interface/cmn/commonInfo';
import { IContent } from 'interface/project/projectInfo';
import { getComparatorAny, stableSort } from 'libs/mindUtils';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import uniqueId from 'lodash/uniqueId';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppDispatch } from 'store';
import { handleChangeMenu } from 'store/menu/menuSlice';
import { openDialog } from 'store/dialog/dialogSlice';
import { openBackdrop, closeBackdrop } from 'store/backdrop/backdropSlice';
import { showMessage } from 'store/message/messageSlice';
import { deleteProject } from 'store/project/projectSlice';
import ProjectDeleteConfirmDialog from './ProjectDeleteConfirmDialog';
import ProjectStateBox from './ProjectStateBox';
import ProjectListTableBodySkeleton from './ProjectListTableBodySkeleton';

function ProjectListBody({ loading, rows, duration }: ICmnTableBodyProps<IContent>): JSX.Element {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { projectListSubSearchCondition, getCommCodes, getProjectList } = useProject();
  const { data: commCodesData } = getCommCodes();
  const { mutate } = getProjectList();

  const handleFindInfoByCodeValue = useCallback(
    (codeValue: string): string => {
      return commCodesData?.find(code => code.codeValue === codeValue)?.codeName || '-';
    },
    [commCodesData],
  );
  const { order, orderBy } = projectListSubSearchCondition;
  const _order = order as Order;
  const _orderBy = orderBy;

  const handleModifyInfo = (event: React.MouseEvent<HTMLButtonElement>, _rowInfo: IContent) => {
    event.preventDefault();
    event.stopPropagation();
    navigate('/admin/project/enter-information', {
      state: {
        codeValue: _rowInfo.projectType,
        rowInfo: _rowInfo,
      },
    });
    dispatch(handleChangeMenu({ changeMenu: ['create'] }));
  };
  const handleProjectManage = useCallback(
    (_rowInfo: IContent) => {
      navigate('/admin/project/management', {
        state: {
          from: location.pathname || '/admin/project/overview',
          codeValue: _rowInfo.projectType,
          rowInfo: _rowInfo,
        },
      });
      dispatch(handleChangeMenu({ changeMenu: ['project'] }));
    },
    [navigate, location.pathname, dispatch],
  );

  const handleRowClick = useCallback(
    (event: React.MouseEvent<HTMLTableRowElement>, _rowInfo: IContent) => {
      // 버튼 클릭 시에는 행 클릭 이벤트가 발생하지 않도록 처리
      const target = event.target as HTMLElement;
      if (target.closest('button') || target.closest('svg')) {
        return;
      }
      handleProjectManage(_rowInfo);
    },
    [handleProjectManage],
  );

  const handleDeleteProject = useCallback(
    (projectId: string, projectName: string) => {
      dispatch(openBackdrop({}));
      dispatch(deleteProject({ projectId }))
        .unwrap()
        .then(response => {
          const { status } = response;
          if (status === 200) {
            dispatch(showMessage({ message: response?.data?.message || '프로젝트가 삭제되었습니다.', variant: 'success' }));
            mutate();
          } else {
            dispatch(
              showMessage({
                message: response?.data?.message || '프로젝트 삭제에 실패했습니다.',
                variant: 'error',
              }),
            );
          }
        })
        .catch(e => {
          console.log(e);
          dispatch(showMessage({ message: '프로젝트 삭제 중 오류가 발생했습니다.', variant: 'error' }));
        })
        .finally(() => {
          dispatch(closeBackdrop());
        });
    },
    [dispatch, mutate],
  );

  const handleDeleteClick = (event: React.MouseEvent<HTMLButtonElement>, _rowInfo: IContent) => {
    event.preventDefault();
    event.stopPropagation();
    dispatch(
      openDialog({
        content: (
          <ProjectDeleteConfirmDialog
            projectName={_rowInfo.projectName}
            handleConfirm={() => handleDeleteProject(_rowInfo.id, _rowInfo.projectName)}
          />
        ),
        disableBackdropClick: true,
        disableEscapeKeyDown: true,
      }),
    );
  };
  return (
    <>
      {loading && duration >= 0.5 && <ProjectListTableBodySkeleton />}
      {!loading && rows.length === 0 && (
        <TableBody>
          <TableRow>
            <CustomTableCell align="center" colSpan={10} sx={{ border: 'none' }}>
              조회된 데이터가 없습니다.
            </CustomTableCell>
          </TableRow>
        </TableBody>
      )}
      {!loading && rows.length !== 0 && (
        <TableBody>
          {stableSort(rows, getComparatorAny(_order, _orderBy) as (a: IContent, b: IContent) => number).map((row, index) => {
            const isItemSelected = false;
            const labelId = `table-checkbox-${index}`;
            return (
              <TableRow
                hover
                role="checkbox"
                aria-checked={isItemSelected}
                tabIndex={-1}
                key={`${row.id}-${uniqueId()}`}
                selected={isItemSelected}
                id={labelId}
                onClick={e => handleRowClick(e, row)}
                sx={{ cursor: 'pointer' }}
              >
                <CustomTableCell align="center" sx={{ width: 120, maxWidth: 120 }}>
                  <ProjectStateBox projectState={handleFindInfoByCodeValue(row.projectState)} />
                </CustomTableCell>
                <CustomTableCell align="center">{handleFindInfoByCodeValue(row.projectType)}</CustomTableCell>
                <CustomTableCell align="center" sx={{ width: 120, maxWidth: 120 }}>
                  <Box
                    sx={{
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                      overflow: 'hidden',
                      display: 'block',
                      width: '100%',
                      textAlign: 'center',
                    }}
                  >
                    {row.projectAdmins.map(obj => obj.name).join(', ')}
                  </Box>
                </CustomTableCell>
                <CustomTableCell align="center">{row.projectName}</CustomTableCell>
                <CustomTableCell align="center">{row.compName}</CustomTableCell>
                <CustomTableCell align="center">{row.projectStartAt}</CustomTableCell>
                <CustomTableCell align="center">{row.projectEndAt}</CustomTableCell>
                <CustomTableCell align="center">{row.projectStatus?.progress || 0}%</CustomTableCell>
                <CustomTableCell align="center">{row.projectStatus?.totalParticipantCount != null ? row.projectStatus.totalParticipantCount : '-'}</CustomTableCell>
                <CustomTableCell align="center">
                  <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                    <IconButton onClick={e => handleModifyInfo(e, row)} disabled={row.projectState !== 'PD'}>
                      <DescriptionOutlinedIcon />
                    </IconButton>
                    <IconButton onClick={e => handleDeleteClick(e, row)} color="error">
                      <DeleteOutlineIcon />
                    </IconButton>
                  </Box>
                </CustomTableCell>
              </TableRow>
            );
          })}
        </TableBody>
      )}
    </>
  );
}

export default memo(ProjectListBody);
